import { useLayoutEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * StaticFallback component that redirects to static HTML files
 * when a route doesn't exist in React. This enables incremental
 * migration from static HTML to React while maintaining backwards compatibility.
 *
 * This component handles:
 * - Routes like /bio -> handled by React (BioPage)
 * - Any other unmatched route -> attempts to load as static HTML
 */
const StaticFallback = () => {
  const location = useLocation();
  const hasRedirected = useRef(false);

  useLayoutEffect(() => {
    // Prevent multiple redirects
    if (hasRedirected.current) {
      return;
    }

    // Get the current pathname
    const pathname = location.pathname;

    // Get the current browser URL pathname (may differ from React Router pathname)
    const currentBrowserPath = window.location.pathname;

    // If we're already on a .html path that matches what we'd redirect to, don't redirect
    // This prevents infinite loops
    if (currentBrowserPath.endsWith('.html') &&
        (currentBrowserPath === pathname || currentBrowserPath === `${pathname}.html`)) {
      // We're already on the target static file, no need to redirect
      return;
    }

    // List of known static HTML files that should fallback
    // Add more as needed when migrating pages to React
    const staticPages = [
      '/contact.html',
      '/accessibility.html',
      '/copyright.html',
    ];

    // Determine the static file path to redirect to
    let staticPath = pathname;

    // If path doesn't end with .html, try adding it
    if (!pathname.endsWith('.html')) {
      staticPath = `${pathname}.html`;
    }

    // Check if this is a known static page (with or without .html)
    const isKnownStaticPage = staticPages.includes(staticPath) ||
                              staticPages.includes(pathname);

    let targetPath = null;

    if (isKnownStaticPage) {
      // Use the .html version for known pages
      targetPath = staticPages.find(p =>
        p === pathname || p === staticPath
      ) || staticPath;
    } else if (pathname.endsWith('.html')) {
      // If path ends with .html, try to load it as a static file
      targetPath = pathname;
    } else if (pathname !== '/') {
      // For other paths, try adding .html extension
      targetPath = staticPath;
    }

    // Only redirect if we have a target and it's different from current browser path
    if (targetPath && targetPath !== currentBrowserPath) {
      hasRedirected.current = true;
      // Use replace to avoid adding to history and force full page reload
      window.location.replace(targetPath);
    }
  }, [location.pathname]);

  // Show a loading message while redirecting
  return (
    <div style={{
      padding: '2rem',
      textAlign: 'center',
      color: 'var(--color-text-light)'
    }}>
      <p>Loading...</p>
    </div>
  );
};

export default StaticFallback;

